import { AddEmail, Email } from "api/interfaces/emailInterface.interface";
import { useCallback, useState } from "react";
import { AddModalPresenter, UPDATE_TYPE } from "./AddModalPresenter";
import { GetUserAllList } from "api/interfaces/userInterface.interface";
import { useQuery } from "react-query";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { getUserAllList } from "api/userAPI";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { Option } from "utils/options";
import useApiError from "hook/useApiError";
import { SearchUserAllList } from "api/interfaces/userInterface.interface";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  isEdit?: boolean;
  selectedEmail?: Email;
  onModal: () => void;
  onClickSendEmail: (email: AddEmail, emailId?: string) => void;
}

export function AddModalContainer(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { isEdit, onModal, onClickSendEmail, selectedEmail } = props;
  const { lang, intlString } = useApiError();

  const [state, setState] = useState<AddEmail>(
    isEdit && selectedEmail !== undefined
      ? {
          subject: selectedEmail.subject,
          message: selectedEmail.message,
          recipientType: selectedEmail.recipientType,
          isSchedule: false,
          scheduleTime: selectedEmail.scheduleTime,
          users: selectedEmail.users,
        }
      : {
          subject: "",
          message: "",
          recipientType: "ALL",
          isSchedule: false,
          scheduleTime: new Date().toISOString(),
          users: [],
        }
  );

  type UserOption = Option & { name: string };
  const [users, setUsers] = useState<UserOption[]>([]);

  const [validate, setValidate] = useState<ValidateMsg>({
    user: "",
    title: "",
  });

  const getUserAll = useQuery(
    ["getAllUser"],
    () =>
      getUserAllList({
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 3,
      onSuccess: (res: GetUserAllList) => {
        setUsers(
          res.result.map((user) => {
            return {
              name: user.name,
              label: `${user.name} [${user.email}]`,
              value: user.email,
            };
          })
        );
      },
      onError: (e: any) => {},
    }
  );

  const onChange = useCallback(
    (type: UPDATE_TYPE, value: any) => {
      if (type === UPDATE_TYPE.SUBJECT) {
        setState((email) => {
          return {
            ...email,
            subject: value,
          };
        });
      } else if (type === UPDATE_TYPE.MESSAGE) {
        setState((email) => {
          return {
            ...email,
            message: value,
          };
        });
      } else if (type === UPDATE_TYPE.EMAIL_TYPE) {
        setState((email) => {
          return {
            ...email,
            recipientType: value,
            users: value === "USER" ? email.users : [],
          };
        });
        if (value !== "USER") {
          setValidate((info) => ({ ...info, user: "" }));
        }
      } else if (type === UPDATE_TYPE.SCHEDULE_TIME) {
        setState((email) => {
          return {
            ...email,
            scheduleTime: value,
          };
        });
      } else if (type === UPDATE_TYPE.USERS) {
        const selectedUsers: SearchUserAllList[] = Array.isArray(value)
          ? value
              .map((item) => {
                if (typeof item === "string") {
                  return item;
                }

                if (
                  item &&
                  typeof item === "object" &&
                  typeof item.value === "string"
                ) {
                  return item.value;
                }

                return null;
              })
              .filter((email): email is string => Boolean(email))
              .map((email) => {
                const user = users.find((u) => u.value === email);

                return {
                  name: user?.name ?? email,
                  email,
                };
              })
          : [];

        setState((prev) => ({
          ...prev,
          users: selectedUsers,
        }));

        setValidate((prev) => ({
          ...prev,
          user: "",
        }));
      }
    },
    [users]
  );

  const onChangeMessage = useCallback((value: string) => {
    onChange(UPDATE_TYPE.MESSAGE, value);
  }, []);

  const onChangeTime = useCallback((value: string) => {
    onChange(UPDATE_TYPE.SCHEDULE_TIME, value);
  }, []);

  const onClickSend = useCallback(
    (isSchedule?: boolean) => {
      let checkError = false;

      setValidate((info) => {
        return { ...info, user: "", title: "" };
      });

      if (state.recipientType === "USER" && state.users?.length === 0) {
        setValidate((info) => {
          return {
            ...info,
            user: intlString(lang.validateMsg.videoShare.err.emptyUser),
          };
        });
        checkError = true;
      }

      if (checkEmpty(state.subject)) {
        setValidate((info) => {
          return {
            ...info,
            title: intlString(lang.validateMsg.setting.email.title.empty),
          };
        });
        setState((info) => ({
          ...info,
          subject: "",
        }));
        checkError = true;
      }
      if (checkEmpty(state.message)) {
        setValidate((info) => {
          return {
            ...info,
            message: intlString(lang.validateMsg.setting.email.message.empty),
          };
        });
        checkError = true;
      }

      if (checkError) return;
      const payload = {
        ...state,
        subject: toTrim(state.subject),
        message: toTrim(state.message),
        isSchedule: isSchedule ? isSchedule : false,
      }

      if (isEdit && selectedEmail) {
        onClickSendEmail(
          payload,
          selectedEmail.emailId
        );
      } else {
        onClickSendEmail(payload);
      }
    },
    [isEdit, onClickSendEmail, selectedEmail, state]
  );

  return (
    <AddModalPresenter
      state={state}
      isEdit={props.isEdit}
      onModal={onModal}
      onClickSend={onClickSend}
      onChange={onChange}
      onChangeMessage={onChangeMessage}
      onChangeTime={onChangeTime}
      users={users}
      usersIsFetching={getUserAll.isFetching}
      validate={validate}
    />
  );
}
